/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/no-danger */

// node modules
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import { DOMParser } from 'xmldom';
import ReactTooltip from 'react-tooltip';
import Skeleton from 'react-loading-skeleton';
import Lightbox from 'react-images';

// app modules: actions
import { setCurrentStep } from 'actions/homeconnect';
import { openOvenMenu } from 'actions/avatarMenu';

// app modules: services
import { getAdjustmentText } from 'components/HomeConnect/Adjustment';
import formatter from 'services/formatter';
import { getStore } from 'store/configureStore';
import { isApp } from 'services/environment';

// app modules: graphics
import RecipeImageProps from 'components/RecipeImage/RecipeImageProps';

import { VALID_APPLIANCE_TYPES } from 'constants/content';

// assets
import CssGeneralContent from './GeneralContent.scss';

const tagRegexSelfClosing = tagName => new RegExp(`<${tagName}([^>]*)\\/>`, 'g');
const tagRegex = tagName => new RegExp(`<(\\/)?${tagName}[^>]*>`, 'g');
const useFullUrl = isApp();

const REGEX = {
  INGREDIENT_PHRASE_TAG_SELF_CLOSING: tagRegexSelfClosing('ingredientphrase'),
  INGREDIENT_PHRASE_TAG: tagRegex('ingredientphrase'),
  INGREDIENT_TAG_SELF_CLOSING: tagRegexSelfClosing('ingredient'),
  INGREDIENT_TAG: tagRegex('ingredient'),
  DESCRIPTOR_TAG_SELF_CLOSING: tagRegexSelfClosing('descriptor'),
  DESCRIPTOR_TAG: tagRegex('descriptor'),
};

// Allow injected links to trigger the oven menu to open
if (typeof window !== 'undefined') {
  window.ckbk = window.ckbk || {};
  window.ckbk.openOvenMenu = (event, currentStepIndex) => {
    event.preventDefault();
    const store = getStore();
    store.dispatch(openOvenMenu());
    store.dispatch(setCurrentStep({ currentStepIndex }));
  };
}
export class GeneralContentComponent extends PureComponent {
  static propTypes = {
    raw: PropTypes.string.isRequired,
    beforeTransform: PropTypes.arrayOf(PropTypes.func),
    homeConnectEnabled: PropTypes.bool,
    homeConnectOvenBrand: PropTypes.string,
    highlightAdjustments: PropTypes.bool,
    userOvenBrands: PropTypes.arrayOf(PropTypes.string),
    loading: PropTypes.bool,
    // eslint-disable-next-line react/forbid-prop-types
    skeletonProps: PropTypes.object,
    dualColumn: PropTypes.bool,
    graphics: RecipeImageProps.graphics,
    baseUrl: PropTypes.string,
  };

  static defaultProps = {
    beforeTransform: [],
    homeConnectEnabled: false,
    homeConnectOvenBrand: '',
    highlightAdjustments: false,
    userOvenBrands: [],
    loading: false,
    skeletonProps: {},
    dualColumn: false,
    graphics: [],
    baseUrl: null,
  };

  constructor(props) {
    super(props);

    this.state = {
      openLightboxImage: null,
    };
  }

  componentDidMount() {
    ReactTooltip.rebuild();
  }

  componentDidUpdate() {
    ReactTooltip.rebuild();
  }

  handleClick = event => {
    if (event.target.tagName === 'A') {
      const { lightboxIdx } = event.target.dataset;
      if (lightboxIdx) {
        event.preventDefault();
        this.setState({ openLightboxImage: parseInt(lightboxIdx, 10) });
      }
    }
  };

  handleTouchMove = event => {
    // Disable swipe to browse from within tables that are wider than the screen
    if (event.target.matches('table, table *')) {
      const table = event.target.closest('table');
      const layoutPadding = 15;
      if (table.offsetWidth + layoutPadding > window.innerWidth) {
        event.stopPropagation();
      }
    }
  };

  render() {
    const {
      raw,
      beforeTransform,
      homeConnectEnabled,
      homeConnectOvenBrand,
      highlightAdjustments,
      userOvenBrands,
      loading,
      skeletonProps,
      dualColumn,
      graphics,
      baseUrl,
    } = this.props;
    const { openLightboxImage } = this.state;

    if (!raw && loading) {
      return (
        <div className="general-content">
          <Skeleton {...skeletonProps} />
        </div>
      );
    }
    const parser = new DOMParser();
    const XML = parser.parseFromString(`<div>${raw}</div>`, 'text/xml');
    const DOM = XML.documentElement;
    beforeTransform.forEach(func => {
      func(DOM);
    });

    // anchors
    formatter.xml.internalizeLinks(DOM);

    // Display direct links to images in a lightbox
    const linkedImageUrls = Array.from(
      new Set(
        Array.from(DOM.getElementsByTagName('a'))
          .filter(a => a.getAttribute('href')?.startsWith('/images/'))
          .map((a, idx) => {
            a.setAttribute('data-lightbox-idx', `${idx}`);

            return {
              src: a.getAttribute('href'),
              srcset: a.getAttribute('data-srcset'),
              caption: a.textContent,
            };
          }),
      ),
    );

    // h3
    Array.prototype.forEach.call(DOM.getElementsByTagName('h3'), heading => {
      heading.setAttribute('class', 'heading heading--size-18 margin-bottom-5');
    });

    // ul
    Array.prototype.forEach.call(DOM.getElementsByTagName('ul'), list => {
      const listType = list.getAttribute('type');
      const listClassName = classNames('list margin-bottom-20', list.getAttribute('class'), {
        'list--dashed list--dashed-mine-shaft': listType === 'dashed',
        'list--normal': listType === 'normal',
        'list--bulleted': listType === 'disc',
        'list--upper-alpha': listType === 'upper-alpha',
        'list--lower-alpha': listType === 'lower-alpha',
        'list--upper-roman': listType === 'upper-roman',
        'list--lower-roman': listType === 'lower-roman',
      });
      list.setAttribute('class', listClassName);
    });

    // ol
    Array.prototype.forEach.call(DOM.getElementsByTagName('ol'), list => {
      const listClassName = classNames(
        'list list--ordered margin-bottom-20',
        list.getAttribute('class'),
      );
      list.setAttribute('class', listClassName);
    });

    // li
    Array.prototype.forEach.call(DOM.getElementsByTagName('li'), listItem => {
      const listItemClassName = classNames('list__item', listItem.getAttribute('class'));
      listItem.setAttribute('class', listItemClassName);
    });

    // images
    Array.prototype.forEach.call(DOM.getElementsByTagName('img'), image => {
      const src = image.getAttribute('src');
      const metadata = graphics.find(graphic => graphic.src === src);

      if (metadata) {
        const originalCrop = metadata.crops?.find(crop => crop.type === 'original');
        const existingClasses = image.getAttribute('class');
        image.setAttribute('class', `scale-with-grid ${existingClasses}`);
        image.setAttribute('loading', 'lazy');
        image.setAttribute('data-lightbox-max-width', `min(100%, ${metadata.width}px)`);

        // We aim to display inline images at no more than 75% of the column width.
        // For narrow displays (<phone-vertical breakpoint) it can scale up to 100% column width.
        const contentWidth = 964;
        // Sometimes there are 2 columns which means we need to half our target size
        const columnWidth = dualColumn ? contentWidth / 2 : contentWidth;
        // Calculate the maximum dimensions for the image based on target width and aspect ratio
        const aspectRatio = metadata ? metadata.width / metadata.height : 1;
        let maxImageWidth = columnWidth * 0.75;
        let maxImageHeight = maxImageWidth / aspectRatio;

        // Cap the height of tall images
        let isTall = false;
        if (maxImageHeight > 500) {
          maxImageHeight = 500;
          maxImageWidth = maxImageHeight * aspectRatio;
          isTall = true;
        }

        // Default case - request an image that will fit into 75% of the column width
        const availableSizes = originalCrop.sizes.slice().reverse();
        const defaultSizeIndex = availableSizes.findIndex(size => size.w < maxImageWidth);
        const defaultSize =
          defaultSizeIndex > 0 ? availableSizes[defaultSizeIndex - 1] : originalCrop.sizes[0];
        image.setAttribute('src', useFullUrl ? `${baseUrl}${defaultSize.src}` : defaultSize.src);

        // If the image is big enough we can let the browser pick from a menu of larger images to satisfy the display pixel density.
        if (metadata?.height >= 400) {
          let sizeOptions;
          if (isTall) {
            // We have a known display height of 500px, so we can use pixel density
            sizeOptions = [];
            [500, 1000, 1500].forEach((height, i) => {
              const option = originalCrop.sizes.find(size => size.h >= height);
              if (option) {
                sizeOptions.push(
                  `${useFullUrl ? `${baseUrl}${option.src}` : option.src} ${i + 1}x`,
                );
              }
            });
          } else {
            // Give the browser a range of options
            sizeOptions = originalCrop.sizes
              .filter(size => size.w >= defaultSize.w)
              .map(size => `${useFullUrl ? `${baseUrl}${size.src}` : size.src} ${size.w}w`);
          }
          if (sizeOptions.length > 1) {
            image.setAttribute('srcset', sizeOptions.join(','));

            if (dualColumn) {
              // dual column - 50vw * 0.75 = 37.5vw
              // if its a narrow display, then fall back to single column (75vw)
              // if its a wide display, the columns max out at 462px * 0.75 = 347px
              image.setAttribute(
                'sizes',
                '(max-width: 640px) 75vw, (min-width: 1024px) 347px, 38vw',
              );
            } else {
              // single column - 100vw * 0.75
              // If its a narrow display we show full width
              // if its a wide display, the column maxes out at 964px * 0.75 = 723px
              image.setAttribute(
                'sizes',
                '(max-width: 480px) 100vw, (min-width: 1024px) 723px, 75vw',
              );
            }
          }
        }
      } else {
        console.warn('No crops found', { src, graphics });
      }
      if (image.parentNode.nodeName === 'figure') {
        const figure = image.parentNode;
        Array.from(figure.getElementsByTagName('figcaption')).forEach(figcaption => {
          if (figcaption.firstChild && figcaption.getAttribute('class') !== 'caption') {
            image.setAttribute('alt', figcaption.firstChild.textContent);
            figure.removeChild(figcaption);
          }
        });
      }
    });

    // p
    Array.prototype.forEach.call(DOM.getElementsByTagName('p'), tag => {
      tag.setAttribute('class', 'general-content__paragraph');
    });

    // applianceAdjustment
    if (highlightAdjustments) {
      if (homeConnectEnabled || userOvenBrands.length) {
        Array.from(DOM.getElementsByTagName('applianceAdjustment'))
          .filter(adjustmentNode =>
            VALID_APPLIANCE_TYPES.includes(
              (adjustmentNode.getAttribute('type') || '').toLowerCase().replace(/[^a-z]/g, ''),
            ),
          )
          .forEach((applianceAdjustment, index) => {
            const anchor = XML.createElement('a');
            anchor.textContent = applianceAdjustment.textContent;
            anchor.setAttribute('class', 'link link--energized');
            if (homeConnectEnabled) {
              anchor.setAttribute('onclick', `ckbk.openOvenMenu(event, ${index});`);
            }
            const setting = Array.from(applianceAdjustment.getElementsByTagName('setting')).filter(
              node => node.parentNode === applianceAdjustment,
            )[0];
            const overrides = Array.from(applianceAdjustment.getElementsByTagName('override'));
            let override;
            if (overrides.length) {
              override = overrides.find(
                _override =>
                  _override.getAttribute('brand').toLowerCase() ===
                    homeConnectOvenBrand.toLowerCase() ||
                  userOvenBrands
                    .map(brand => brand.toLowerCase())
                    .includes(_override.getAttribute('brand').toLowerCase()),
              );
              if (override) {
                const brand = override.getAttribute('brand');
                const settingNode = override.getElementsByTagName('setting')[0];
                const durationNode = override.getElementsByTagName('duration')[0];
                const altText =
                  (settingNode ? settingNode.getAttribute('alt') : null) ||
                  override.getAttribute('alt');
                let settingText;
                if (settingNode) {
                  const parsedOverride = {
                    type: override.getAttribute('type'),
                    setting: {
                      mode: settingNode.getAttribute('mode'),
                      unit: settingNode.getAttribute('unit'),
                      value: settingNode.getAttribute('value'),
                      fanValue: settingNode.getAttribute('fanValue') || undefined,
                      rapidHeating:
                        settingNode.getAttribute('rapidHeating') === 'true' ? true : undefined,
                      addedMoisture: settingNode.getAttribute('addedMoisture') || undefined,
                      text: settingNode.textContent || undefined,
                    },
                  };

                  if (durationNode) {
                    parsedOverride.duration = {
                      value: durationNode.getAttribute('value'),
                    };
                  }
                  settingText = getAdjustmentText(parsedOverride);
                }
                if (altText || settingText) {
                  const text = [altText, settingText].join('<br/>');
                  anchor.setAttribute(
                    'data-tip',
                    `<p><strong>For ${brand.toUpperCase()} ovens:</strong> ${text}</p>`,
                  );
                }
              }
            }
            if ((homeConnectEnabled && setting) || override) {
              applianceAdjustment.parentNode.replaceChild(anchor, applianceAdjustment);
            }
          });
      }
    }

    const html = DOM.childNodes
      .toString()
      .replace(REGEX.INGREDIENT_PHRASE_TAG_SELF_CLOSING, '')
      .replace(REGEX.INGREDIENT_PHRASE_TAG, '')
      .replace(REGEX.INGREDIENT_TAG_SELF_CLOSING, '<b></b>')
      .replace(REGEX.INGREDIENT_TAG, '<$1b>')
      .replace(REGEX.DESCRIPTOR_TAG_SELF_CLOSING, '<span></span>')
      .replace(REGEX.DESCRIPTOR_TAG, '<$1span>');

    return (
      <React.Fragment>
        <div
          onClick={this.handleClick}
          onTouchMove={this.handleTouchMove}
          className="general-content"
          dangerouslySetInnerHTML={{ __html: html }}
        />
        {openLightboxImage !== null ? (
          <Lightbox
            backdropClosesModal
            images={linkedImageUrls}
            isOpen
            currentImage={openLightboxImage}
            onClose={() => {
              this.setState({ openLightboxImage: null });
            }}
            showImageCount={false}
            onClickNext={() => {
              this.setState({ openLightboxImage: openLightboxImage + 1 });
            }}
            onClickPrev={() => {
              this.setState({ openLightboxImage: openLightboxImage - 1 });
            }}
            width={2000}
          />
        ) : null}
      </React.Fragment>
    );
  }
}

const mapStateToProps = state => ({
  baseUrl: state.config.baseUrl,
  homeConnectEnabled: !!state.user.home_connect_enabled,
  userOvenBrands: state.user.oven_brands,
  homeConnectOvenBrand:
    state.homeConnect.primaryOven && state.homeConnect.primaryOven.brand
      ? state.homeConnect.primaryOven.brand
      : '',
});

const mapDispatchToProps = {};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(CssGeneralContent)(GeneralContentComponent));
